import { createFileRoute, type LinkProps, useRouteContext } from "@tanstack/react-router";
import { useMemo, type JSX } from "react";
import { z } from "zod";

import { graphql } from "@repo/graphql-types/gql";
import Car from "@repo/icons/default/Car";
import Document from "@repo/icons/default/Document";
import Key from "@repo/icons/default/Key";
import Left from "@repo/icons/default/Left";
import User1 from "@repo/icons/default/User1";
import UserIdentity from "@repo/icons/default/UserIdentity";
import { CpsBadge } from "corpus";

import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import { CustomWebsiteCard } from "@/components/site/website/custom-website-card";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import {
  MenuList,
  MenuListCard,
  type MenuListItem,
} from "@/components/ui/layout/menu/menu-list";
import { MyProfileDetailsSection } from "@/components/profile/sections/my-profile-details-section";
import { Page } from "@/components/ui/layout/page/page";
import { ReferralBanner } from "@/components/referral/referral-banner";
import { useBannerDismissed } from "@/hooks/use-banner-dismissed.ts";
import { useGraphQL } from "@/hooks/use-graphql";
import { shouldShowComponentByFF } from "@/utils/should-show-component-by-ff";

const MyProfilePageQuery = graphql(/* GraphQL */ `
  query MyProfilePageQuery($codUsuario: Int!) {
    usuarioPlano: usuariosProfissionaisPlanos(where: { ativo: { _eq: true } }) {
      plano {
        nomeExibicao
      }
    }
    solicitacaoCancelamentoAssinatura: solicitacoesCancelamento(
      where: {
        solicitacaoAtendida: { _eq: false }
        _or: [
          { codStatusCancelamento: { _eq: 10 } }
          { codStatusCancelamento: { _eq: 20 } }
        ]
      }
      limit: 1
      order_by: { dataCadastro: desc }
    ) {
      codSolicitacaoCancelamento
    }
    ...MyProfileDetailsSectionFragment
    ...PublicPhoneNumberFragment
    ...CustomWebsiteCardFragment
  }
`);

const profileSchema = z.object({
  action: z.string().optional(),
});

const MyProfilePage = (): JSX.Element => {
  const { user, flags } = useRouteContext({ from: "__root__" });

  const {
    isDismissed,
    isLoading: isBannerLoading,
    dismissBanner,
  } = useBannerDismissed("referralHomeBanner");

  const { routesWithBannerReferral } = useMemo(
    () => flags["exibir-banner-de-gentileza-gera-gentileza"],
    [flags],
  );

  const menuConfig = useMemo(
    () => flags["exibicao-itens-menu-pagina-meu-perfil"],
    [flags],
  );

  const shouldShowCancellationStatus = useMemo(
    () => flags["exibe-cancelar-assinatura"],
    [flags],
  );

  const queryResult = useGraphQL(MyProfilePageQuery, {
    codUsuario: Number(user.codUsuario),
  });

  const { data, isError } = queryResult;

  const planName =
    data && "usuarioPlano" in data
      ? String(
          (data.usuarioPlano as { plano: { nomeExibicao?: string } }[])[0]?.plano
            ?.nomeExibicao ?? "",
        )
      : "";

  const hasSubscriptionCancellation =
    data && "solicitacaoCancelamentoAssinatura" in data
      ? Array.isArray(data.solicitacaoCancelamentoAssinatura) &&
        data.solicitacaoCancelamentoAssinatura.length > 0
      : false;

  const personalDataMenuItems: (MenuListItem & { show: boolean })[] = [
    {
      name: "Dados pessoais",
      Icon: User1,
      route: "/settings/profile/personal-data" as LinkProps["to"],
      show: menuConfig.personalDataMenuItems.personalData,
    },
    {
      name: "Dados profissionais",
      Icon: UserIdentity,
      route: "/settings/profile/professional-data" as LinkProps["to"],
      show: menuConfig.personalDataMenuItems.professionalData,
    },
    {
      name: "Veículos",
      Icon: Car,
      route: "/settings/profile/vehicles" as LinkProps["to"],
      show: menuConfig.personalDataMenuItems.cars,
    },
  ].filter((item) => item.show);

  const planAndAccessMenuItems: (MenuListItem & { show: boolean })[] = [
    {
      name: "Meu plano",
      description: planName,
      Icon: Document,
      route: "/settings/profile/plan" as LinkProps["to"],
      tag:
        hasSubscriptionCancellation && shouldShowCancellationStatus ? (
          <CpsBadge color="danger">Em cancelamento</CpsBadge>
        ) : (
          <CpsBadge color="success">Ativo</CpsBadge>
        ),
      show: menuConfig.planAndAccessMenuItems.yourPlan,
    },
    {
      name: "Informações de acesso",
      Icon: Key,
      route: undefined,
      show: menuConfig.planAndAccessMenuItems.accessInfo,
    },
  ].filter((item) => item.show);

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Meu perfil" align="center" />
      </HeaderRoot>
      <Page className="px-6">
        <AsyncDataWrapper {...queryResult}>
          {data ? (
            <div className="flex flex-col gap-4">
              <MyProfileDetailsSection isError={isError} queryData={data} />

              {menuConfig.personalDataMenuItems.showSection &&
              personalDataMenuItems.length > 0 ? (
                <MenuListCard>
                  <MenuList menuItems={personalDataMenuItems} />
                </MenuListCard>
              ) : null}

              {menuConfig.planAndAccessMenuItems.showSection &&
              planAndAccessMenuItems.length > 0 ? (
                <MenuListCard>
                  <MenuList menuItems={planAndAccessMenuItems} />
                </MenuListCard>
              ) : null}

              <CustomWebsiteCard data={data} />

              {shouldShowComponentByFF(routesWithBannerReferral) &&
              !isDismissed &&
              !isBannerLoading ? (
                <ReferralBanner source="profile" onDismiss={dismissBanner} />
              ) : null}
            </div>
          ) : null}
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/profile/")({
  component: MyProfilePage,
  validateSearch: profileSchema,
});
